/*
 * MEPP - A web application to guide patients and clinicians in the process of
 * facial palsy rehabilitation, with the help of the mirror effect and principles
 * of motor learning
 * Copyright (C) 2021 MEPP <info@mirroreffectplus.org>
 *
 * This file is part of MEPP.
 *
 * MEPP is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * MEPP is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with MEPP.  If not, see <http://www.gnu.org/licenses/>.
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLogout } from 'react-admin';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { temporaryProfil } from '@admin/authProvider';

import IconSettings from '@assets/icons/settings.svg';
import Logo from '@assets/logos/logo.svg';

import { media } from '@styles/configs/breakpoints';
import { spacings } from '@styles/configs/spacings';
import { FlexAlignMiddle, FlexDisplay } from '@styles/tools';

import {
  ExerciseStep,
  ExerciseContext,
} from '@components/exercises/ExerciseProvider';
import LocaleSwitcher from '@components/generics/LocaleSwitcher';
import Button from '@components/generics/buttons/Button';
import { OverlayContext } from '@components/overlays/OverlayProvider';
import { SettingsOverlay } from '@components/overlays/SettingsOverlay';

/**
 * Header with Logo and login navigation
 */
const Header = ({ isLogged, hidden }) => {
  return (
    <Container hide={hidden}>
      <LeftSide />
      {!isLogged ? (
        <RightSideWithoutLogout />
      ) : !temporaryProfil ? (
        <RightSideWithLogout />
      ) : (
        <></>
      )}
    </Container>
  );
};

const LeftSide = () => {
  return (
    <LeftWrapper>
      <a href="/" tabIndex="-1">
        <Logo
          width="100%"
          height="100%"
          aria-label="Mirror effect plus protocol logo"
        />
      </a>
    </LeftWrapper>
  );
};

const RightSideWithoutLogout = () => {
  return (
    <RightWrapper>
      <LocaleSwitcher />
    </RightWrapper>
  );
};

const RightSideWithLogout = () => {
  const { t } = useTranslation();
  const logout = useLogout();
  const { content, open, close } = useContext(OverlayContext);
  const [isSettingsActive, setIsSettingsActive] = useState(false);
  const { pause, logs, exerciseStep } = useContext(ExerciseContext);

  // watch overlay close to deactivate settings click state
  useEffect(() => {
    if (!content) setIsSettingsActive(false);
  }, [content]);

  // setting click handler
  const handleSettingsClick = useCallback(() => {
    // if exercises page and it is running, pause current exercise
    if (
      exerciseStep !== ExerciseStep.INITIATED &&
      exerciseStep !== ExerciseStep.ENDED &&
      exerciseStep !== ExerciseStep.EMPTY &&
      exerciseStep !== ExerciseStep.COMPLETED &&
      pause
    ) {
      pause();
    }
    isSettingsActive ? close() : open(<SettingsOverlay />);
    setIsSettingsActive(!isSettingsActive);
  }, [exerciseStep, isSettingsActive]);

  const handleSupportClick = (e) => {
    e.preventDefault();
    window.location.href = '#/support';
  };

  // logout click handler
  const handleLogoutClick = () => {
    if (logs) {
      logs('LOGOUT');
      // timeout to prevent logs request cancel
      // logs will be canceled if taken more than .5s
      setTimeout(() => {
        logout();
      }, 500);
    } else logout();
  };

  return (
    <RightWrapper>
      <Button.Rounded
        aria-label={t('a11y:support')}
        label="?"
        onClick={handleSupportClick}
      />
      <Button.Transparent
        aria-label={t('a11y:settings')}
        icon={<IconSettings width="100%" height="100%" />}
        selected={isSettingsActive}
        onClick={handleSettingsClick}
      />
      <Button.Logout label={t('cta:logout')} onClick={handleLogoutClick} />
    </RightWrapper>
  );
};

const Container = styled.header`
  ${FlexDisplay.CSS}
  box-sizing: border-box;
  padding: ${spacings.default * 2}px;
  width: 100%;
  position: relative;
  transition: top 0.5s ease;
  top: 0;

  ${({ hide }) => hide && `top:-130px;`}

  ${media.xsOnly`
    padding: ${spacings.default}px;
  `}
`;

const LeftWrapper = styled.div`
  width: 185px;
  height: 55px;

  ${media.xsOnly`
    width: 126px;
  `}
`;

const RightWrapper = styled(FlexAlignMiddle.Component)`
  margin-left: auto;

  button {
    margin: 0 ${spacings.default}px;
    &:first-child,
    &:last-child {
      margin-right: 0;
    }
    &:last-child {
      margin-left: 0;
    }

    ${media.xsOnly`
      margin: 0 ${spacings.default / 2}px;
    `}
  }
`;

export { Header };
